import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button} from '@mui/material';
import {KeySnapshot, KeyTransaction, KeyVault} from '../../api/Model';
import {today} from '../../util/DateUtil';
import {useAuthStore} from '../../app-layout/authStore';
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {executeKeyTransaction} from "../../api/KeyTransaction";
import {useFormValidation} from "../../components/form-validation/useFormValidation";
import {CollectKeyForm, CollectUnknownKeyForm} from "./CollectUnknownKeyForm";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {useMessage} from "../../util/actions/Message";
import {useFilter} from "../../util/FilterSerializer";
import {
    filterKeySelectTable,
    KeySelectTableFilter,
    keySelectTableFilterFormSerializer
} from "../../components/key-select-table/KeySelectTableFilter";
import {KeySelectTable} from "../../components/key-select-table/KeySelectTable";
import {useVaultKeysStore} from "../../api/VaultKeysStore";
import {KeyOperationDeprecationWarning} from "../../components/deprecation-warning/DeprecationWarning";

export function CollectKeyPage({defaultKeyVault, keyVaults}: {
    defaultKeyVault: KeyVault,
    keyVaults: KeyVault[],
}) {
    const message = useMessage();
    const filterSerializer = useMemo(
        () => keySelectTableFilterFormSerializer(defaultKeyVault, keyVaults),
        [defaultKeyVault, keyVaults],
    );
    const [filter, setFilter] = useFilter(filterSerializer);
    const vaultKeys = useVaultKeysStore(state => state.remoteValue);
    const keys = useMemo(
        () => vaultKeys.map(currentKeys => currentKeys.withPerson(filter.keyVault)),
        [filter.keyVault, vaultKeys],
    )
    const {
        formError,
        fieldErrors,
        validate: validateForm,
    } = useFormValidation<'key-vault'>();
    const [selectedKeys, setSelectedKeys] = useState<Set<KeySnapshot>>(new Set());
    const [txInProgress, setTxInProgress] = useState(false);

    const user = useAuthStore(state => state.user);

    const handleOpenCollectUnknownKeyDialog = useFormDialogAction({
        title: 'Collect Unknown Key',
        description: 'Enter the key number to collect a key that is not in the list.',
        submitButtonText: 'Collect Key',
        renderForm: CollectUnknownKeyForm,
        action: openDialog => openDialog({
            initialValue: {keyVault: filter.keyVault},
            onSubmit: async (form: CollectKeyForm) => {
                if (!form.keyNumber) return;

                await executeKeyTransaction([{
                    keyNumber: form.keyNumber,
                    vault: form.keyVault,
                    date: today(),
                    newLocation: {type: 'with-vault'},
                    notes: '',
                }]);
            },
            successMessage: form => `Key ${form.keyNumber} collected`,
            errorMessage: 'Failed to collect key',
        }),
    }, [filter.keyVault, user]);

    useEffect(() => {
        setSelectedKeys(new Set());
    }, [keys]);

    const filteredKeys = useMemo(() => {
        return keys.map(allKeys => filterKeySelectTable(allKeys, filter));
    }, [keys, filter]);

    const handleCollectKeys = async () => {
        const isFormValid = validateForm({
            form: selectedKeys.size === 0 && 'Please select at least one key to collect',
            "key-vault": !filter.keyVault && 'Please select a vault',
        });
        if (!filter.keyVault || !isFormValid) return;

        // Create transactions to collect keys to the vault
        const transactions: KeyTransaction[] = Array.from(selectedKeys, key => ({
            keyNumber: key.key.keyNumber,
            vault: key.historyRecord.vault,
            date: today(),
            newLocation: {type: 'with-vault'},
            notes: '',
        }));

        setTxInProgress(true);
        await message.exec(
            async () => {
                await executeKeyTransaction(transactions);
                setSelectedKeys(new Set());
            },
            selectedKeys.size === 1
                ? 'Collected 1 key successfully!'
                : `Collected ${selectedKeys.size} keys successfully!`,
            'Failed to collect keys. Please try again.',
        )
        setTxInProgress(false);
    };

    return (
        <Page
            header="Collect Keys"
            description={<KeyOperationDeprecationWarning/>}
            counter={<StorePageCount totalValue={keys} value={filteredKeys}/>}
            error={formError}
            actions={
                <Box display="flex" gap={2}>
                    <Button
                        variant="outlined"
                        color="primary"
                        disabled={!filter.keyVault}
                        onClick={handleOpenCollectUnknownKeyDialog}
                    >
                        Not Listed...
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={selectedKeys.size === 0 || txInProgress}
                        onClick={handleCollectKeys}
                    >
                        {txInProgress ? (
                            'Collecting Keys...'
                        ) : selectedKeys.size === 1 ? (
                            `Collect ${selectedKeys.size} Key`
                        ) : (
                            `Collect ${selectedKeys.size} Keys`
                        )}
                    </Button>
                </Box>
            }
            filter={
                <KeySelectTableFilter form={filter} setForm={setFilter} fieldErrors={fieldErrors}/>
            }
        >
            <LoadingData
                totalValue={keys}
                value={filteredKeys}
                emptyMessage="No keys from this vault to collect"
                noMatchMessage="No keys matching the search criteria"
                defaultErrorMessage="Failed to load keys. Please try again later."
            >{resolvedKeys =>
                <KeySelectTable keys={resolvedKeys} selectedKeys={selectedKeys} setSelectedKeys={setSelectedKeys}/>
            }</LoadingData>
        </Page>
    );
}
